import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import identityDisclosureService, {
  IdentityDisclosureRequestDetails
} from '../services/identityDisclosureService';
import { formatMailingAddressForDisplay } from '../utils/mailingAddressFormat';

const IdentityDisclosureRequestPage: React.FC = () => {
  const { requestId } = useParams<{ requestId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [request, setRequest] = useState<IdentityDisclosureRequestDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [decisionNotes, setDecisionNotes] = useState('');
  const [savingDecision, setSavingDecision] = useState(false);
  const requestedDecision = (searchParams.get('decision') || '').toLowerCase();

  const loadRequest = async () => {
    if (!requestId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await identityDisclosureService.getRequest(requestId);
      setRequest(data);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Could not load request details.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadRequest();
  }, [requestId]);

  const submitDecision = async (approve: boolean) => {
    if (!requestId) return;
    setSavingDecision(true);
    try {
      const updated = await identityDisclosureService.decide(requestId, {
        approve,
        notes: decisionNotes || undefined
      });
      setRequest(updated);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Could not submit decision.');
    } finally {
      setSavingDecision(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!request) {
    return <Alert severity="warning">Request not found.</Alert>;
  }

  const evidence = request.evidence;

  return (
    <Stack spacing={2}>
      <Typography variant="h5">ID Verification Request</Typography>
      <Card>
        <CardContent>
          <Stack spacing={1}>
            <Typography variant="body2"><strong>Status:</strong> {request.status}</Typography>
            <Typography variant="body2"><strong>Requester:</strong> @{request.requesterUsername} ({request.requesterDisplayName})</Typography>
            <Typography variant="body2"><strong>Requested identity photos:</strong> {request.requestIdentityPhotos ? 'Yes' : 'No'}</Typography>
            <Typography variant="body2"><strong>Requested ID-confirmed mailing address:</strong> {request.requestMailingAddress ? 'Yes' : 'No'}</Typography>
            <Typography variant="body2"><strong>Fee paid by requester:</strong> ${request.feeAmount.toFixed(2)}</Typography>
            {request.recipientViewedAtUtc && (
              <Typography variant="body2"><strong>Recipient viewed:</strong> {new Date(request.recipientViewedAtUtc).toLocaleString()}</Typography>
            )}
            {request.decisionAtUtc && (
              <Typography variant="body2"><strong>Decision time:</strong> {new Date(request.decisionAtUtc).toLocaleString()}</Typography>
            )}
            {request.decisionNotes && (
              <Typography variant="body2"><strong>Decision note:</strong> {request.decisionNotes}</Typography>
            )}
          </Stack>
        </CardContent>
      </Card>

      {evidence && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Verified Identity Data</Typography>
            <Stack spacing={1}>
              {evidence.verifiedFullName && <Typography variant="body2"><strong>Verified name:</strong> {evidence.verifiedFullName}</Typography>}
              {(evidence.idState || evidence.idCountry) && (
                <Typography variant="body2"><strong>ID location:</strong> {[evidence.idState, evidence.idCountry].filter(Boolean).join(', ')}</Typography>
              )}
              {request.requestMailingAddress && evidence.mailingAddressLine1 && (
                <Typography variant="body2">
                  <strong>ID-confirmed mailing address:</strong>{' '}
                  {formatMailingAddressForDisplay(evidence)}
                </Typography>
              )}
            </Stack>
            <Divider sx={{ my: 2 }} />
            <Stack spacing={2}>
              {request.requestIdentityPhotos && evidence.portraitImageUrl && (
                <Box>
                  <Typography variant="subtitle2">ID portrait</Typography>
                  <Box component="img" src={evidence.portraitImageUrl} alt="ID portrait" sx={{ mt: 1, maxWidth: 360, width: '100%', borderRadius: 1 }} />
                </Box>
              )}
              {request.requestIdentityPhotos && evidence.livenessVideoUrl && (
                <Box>
                  <Typography variant="subtitle2">Didit live capture</Typography>
                  <Box component="video" controls src={evidence.livenessVideoUrl} sx={{ mt: 1, maxWidth: 420, width: '100%', borderRadius: 1 }} />
                </Box>
              )}
            </Stack>
          </CardContent>
        </Card>
      )}

      {request.canDecide && (
        <Card>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>Approve or deny request</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              You are not required to respond. If you prefer a different photo, re-verify your ID first.
            </Typography>
            {(requestedDecision === 'approve' || requestedDecision === 'deny') && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Quick action selected from message link: <strong>{requestedDecision.toUpperCase()}</strong>.
                Confirm below to submit.
              </Alert>
            )}
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Optional note"
              value={decisionNotes}
              onChange={(e) => setDecisionNotes(e.target.value)}
            />
            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
              <Button variant="contained" color="success" disabled={savingDecision} onClick={() => submitDecision(true)}>
                Approve
              </Button>
              <Button variant="contained" color="error" disabled={savingDecision} onClick={() => submitDecision(false)}>
                Deny
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      <Box>
        <Button variant="text" onClick={() => navigate('/messages')}>Back to Messages</Button>
      </Box>
    </Stack>
  );
};

export default IdentityDisclosureRequestPage;
